/**
 * Point-cloud debug views for the V7 shell-band voxel field.
 */

import { BufferGeometry, Color, Float32BufferAttribute, Matrix4 } from 'three';
import type { VoxelGrid } from '@modules/three/common/voxels';

const DEFAULT_MAX_POINTS = 80_000;

const COMPONENT_PALETTE = [
  0x22d3ee, 0xf97316, 0xa78bfa, 0x4ade80, 0xf472b6, 0xfacc15, 0x38bdf8, 0xfb7185,
];

export type BandVoxelDebugLayers = {
  /** Shell-band voxels (negative SDF in the wall iso-range). */
  band: BufferGeometry;
  /** Dilated trim barrier on the band (blocks flood). */
  barrier?: BufferGeometry;
  /** Band voxels removed by gingival flood. */
  flooded?: BufferGeometry;
};

export type BuildBandVoxelDebugOptions = {
  maxPointsPerLayer?: number;
  barrier?: Uint8Array;
  flooded?: Uint8Array;
  /** Per-voxel component id for band coloring (from labelBandComponents). */
  componentLabels?: Int32Array;
  /** Transform aligned-grid positions back to world space. */
  inverseAlignMatrix?: Matrix4;
};

export function buildBandVoxelDebugLayers(
  grid: VoxelGrid,
  options: BuildBandVoxelDebugOptions = {},
): BandVoxelDebugLayers {
  const maxPoints = options.maxPointsPerLayer ?? DEFAULT_MAX_POINTS;
  const matrix = options.inverseAlignMatrix;

  const band = buildPointLayer(
    grid,
    (idx) => grid.data[idx] < 0,
    maxPoints,
    options.componentLabels,
    matrix,
  );

  const barrier =
    options.barrier &&
    buildPointLayer(
      grid,
      (idx) => grid.data[idx] < 0 && options.barrier![idx] === 1,
      maxPoints,
      undefined,
      matrix,
    );

  const flooded =
    options.flooded &&
    buildPointLayer(
      grid,
      (idx) => grid.data[idx] < 0 && options.flooded![idx] === 1,
      maxPoints,
      undefined,
      matrix,
    );

  return { band, barrier: barrier || undefined, flooded: flooded || undefined };
}

function buildPointLayer(
  grid: VoxelGrid,
  include: (idx: number) => boolean,
  maxPoints: number,
  componentLabels: Int32Array | undefined,
  matrix: Matrix4 | undefined,
): BufferGeometry {
  const { total, cellSize, origin } = grid.info;
  const indices: number[] = [];
  for (let i = 0; i < total; i++) {
    if (include(i)) indices.push(i);
  }

  const stride = indices.length <= maxPoints ? 1 : Math.ceil(indices.length / maxPoints);
  const count = Math.ceil(indices.length / stride);
  const positions = new Float32Array(count * 3);
  const colors = componentLabels ? new Float32Array(count * 3) : null;
  const color = new Color();
  let out = 0;

  for (let k = 0; k < indices.length; k += stride) {
    const idx = indices[k];
    const { x, y, z } = grid.coords(idx);
    const wx = origin.x + x * cellSize;
    const wy = origin.y + y * cellSize;
    const wz = origin.z + z * cellSize;
    if (matrix) {
      const e = matrix.elements;
      positions[out * 3] = e[0] * wx + e[4] * wy + e[8] * wz + e[12];
      positions[out * 3 + 1] = e[1] * wx + e[5] * wy + e[9] * wz + e[13];
      positions[out * 3 + 2] = e[2] * wx + e[6] * wy + e[10] * wz + e[14];
    } else {
      positions[out * 3] = wx;
      positions[out * 3 + 1] = wy;
      positions[out * 3 + 2] = wz;
    }
    if (colors && componentLabels) {
      const comp = componentLabels[idx];
      color.setHex(COMPONENT_PALETTE[(comp >= 0 ? comp : 0) % COMPONENT_PALETTE.length]);
      colors[out * 3] = color.r;
      colors[out * 3 + 1] = color.g;
      colors[out * 3 + 2] = color.b;
    }
    out++;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions.subarray(0, out * 3), 3));
  if (colors) {
    geometry.setAttribute('color', new Float32BufferAttribute(colors.subarray(0, out * 3), 3));
  }
  return geometry;
}
